import type { Client } from 'typesense';
import type { SearchResponseFacetCountSchema } from 'typesense/lib/Typesense/Documents';

import {
	SEARCH_DECISION_MAKER_MATCHING,
	SEARCH_DECISION_MAKER_SYNONYM_SET,
	SEARCH_DECISION_MAKERS_FILTER_BY,
} from '#src/app/pages/search/shared/consts';

import { buildTypesenseSearchParams, HITS_PER_PAGE } from './buildSearchParams';
import { matchDecisionMaker, toDecisionMakerLookupQuery } from './matchDecisionMaker';
import type {
	DecisionMakerHitData,
	DecisionMakerMatch,
	FacetItem,
	PetitionHitData,
	PetitionSearchParams,
	SearchResultsData,
} from './types';

const CACHE_SEARCH_RESULTS_FOR_SECONDS = 120;

function parseTopicFacets(
	facetCounts: Array<SearchResponseFacetCountSchema<PetitionHitData>> | undefined,
	selectedTopics: string[],
): FacetItem[] {
	if (!facetCounts) return [];
	const tagsFacet = facetCounts.find((f) => f.field_name === 'tags');
	if (!tagsFacet?.counts) return [];
	return tagsFacet.counts.map((c) => ({
		value: c.value,
		label: c.value,
		count: c.count,
		isRefined: selectedTopics.includes(c.value),
	}));
}

/**
 * Typesense answers 404 "Synonym index not found" when a search names a synonym set the server doesn't have
 */
function isMissingSynonymSetError(error: unknown): boolean {
	if (!(error instanceof Error)) return false;
	const { httpStatus } = error as Error & { httpStatus?: number };
	return httpStatus === 404 && /synonym/i.test(error.message);
}

export type SearchClient = {
	searchPetitions: (params: PetitionSearchParams) => Promise<SearchResultsData>;
	/** The decision maker the query names, if there's one worth showing */
	searchDecisionMaker: (query: string, abortSignal?: AbortSignal) => Promise<DecisionMakerMatch | null>;
};

type CreateSearchClientOptions = {
	collectionName: string;
	preset: string;
	decisionMakersCollectionName: string;
	/** Called (once per client) when the server has no nickname synonym set, so the lookup runs without nicknames */
	onMissingSynonymSet?: (error: Error) => void;
};

/**
 * Factory that abstracts the Typesense client behind domain functions. Pure and isomorphic
 * (no React/window deps) so it can be unit-tested with a mock client and reused in SSR prefetch.
 */
export function createSearchClient(
	client: Client,
	{ collectionName, preset, decisionMakersCollectionName, onMissingSynonymSet }: CreateSearchClientOptions,
): SearchClient {
	let missingSynonymSetReported = false;

	return {
		async searchPetitions(params: PetitionSearchParams): Promise<SearchResultsData> {
			const searchParams = buildTypesenseSearchParams(params, preset);

			const response = await client
				.collections<PetitionHitData>(collectionName)
				.documents()
				.search(searchParams, { cacheSearchResultsForSeconds: CACHE_SEARCH_RESULTS_FOR_SECONDS });

			return {
				hits: response.hits?.map((h) => h.document) ?? [],
				nbHits: response.found,
				nbPages: Math.ceil(response.found / HITS_PER_PAGE),
				query: response.request_params.q || params.query,
				topicFacets: parseTopicFacets(response.facet_counts, params.topics),
			};
		},

		async searchDecisionMaker(query: string, abortSignal?: AbortSignal): Promise<DecisionMakerMatch | null> {
			const searchCandidates = async (synonymSets: string | undefined) =>
				client
					.collections<DecisionMakerHitData>(decisionMakersCollectionName)
					.documents()
					.search(
						{
							q: toDecisionMakerLookupQuery(query),
							query_by: 'name',
							num_typos: 0,
							// "smith" shouldn't spend candidate slots on "Smithson"
							prefix: false,
							// every query word must match the name (exactly or through a nickname synonym): the matcher relies on it
							drop_tokens_threshold: 0,
							...(synonymSets ? { synonym_sets: synonymSets } : {}),
							filter_by: `${SEARCH_DECISION_MAKERS_FILTER_BY} && petition_count:>=${SEARCH_DECISION_MAKER_MATCHING.minPetitions}`,
							sort_by: '_text_match:desc,petition_count:desc',
							per_page: SEARCH_DECISION_MAKER_MATCHING.candidates,
							include_fields: 'id,name,title,photo_url,slug,petition_count,verified_at,targetable_type',
						},
						// no Typesense cache: it keeps a rejected (e.g. aborted) request cached; react-query caches the result instead
						{ abortSignal },
					);

			let response;
			try {
				response = await searchCandidates(SEARCH_DECISION_MAKER_SYNONYM_SET);
			} catch (error) {
				// a Typesense server without the synonym set (e.g. a new environment) answers 404: match without nicknames there
				if (!isMissingSynonymSetError(error)) throw error;
				// recovered, but nicknames silently stop matching: say so once rather than on every lookup
				if (!missingSynonymSetReported) {
					missingSynonymSetReported = true;
					onMissingSynonymSet?.(error as Error);
				}
				response = await searchCandidates(undefined);
			}

			return matchDecisionMaker(
				query,
				(response.hits ?? []).map((h) => h.document),
				SEARCH_DECISION_MAKER_MATCHING,
			);
		},
	};
}
